import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fmtMoney, getLang, useT } from '../lib/i18n';
import { fmtLongDay, fmtShortDay, fmtTime } from '../lib/time';
import { getToken } from '../lib/api';
import { alert, confirm, haptic } from '../lib/telegram';
import { MethodPicker, methodLabel, rememberMethod, useMethod, type PayMethod } from './MethodPicker';
import { PaySwitchSheet } from './PaySwitch';

/**
 * «Оплата: <клиент>» as one receipt (docs/design/lesson-payment.md; server:
 * booking/receipts.ts). The balance first, the rest in money with its
 * method, «+ Оплатить следующее занятие», and the line that says where
 * every euro goes before it is saved. Then «Изменить оплату» on a paid
 * lesson, and «История оплат» on the client card.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';
async function api<R>(path: string, init: RequestInit = {}): Promise<R> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b.error ?? `http_${res.status}`);
  }
  return res.json();
}
type T = ReturnType<typeof useT>;

const r2 = (n: number) => Math.round(n * 100) / 100;
/** «0,25» in Russian, «0.25» in English. */
const cr = (n: number) => {
  const v = String(Math.round(n * 100) / 100);
  return getLang() === 'ru' ? v.replace('.', ',') : v;
};
const dayShort = (iso: string | Date) => fmtShortDay(new Date(iso));

/** A refusal in the coach's words. */
export function receiptErrorText(t: T, code: string) {
  const known = ['prepay_gone', 'changed_since', 'credits_used', 'read_only', 'more_is_new_payment',
    'method_required', 'already_undone', 'nothing_to_pay'];
  return known.includes(code) ? t(`lp.err.${code}` as 'lp.err.read_only') : t('lp.err.other', { code });
}

interface Info {
  owed: number; mode: string; startsAt: string; minutes: number;
  balance: { credits: number; value: number; covers: number; perCredit: number | null; payerName: string | null };
  rest: number;
  next: { bookingId: string; startsAt: string; minutes: number; title: string; price: number; mode: 'cash' | 'credits'; credits: number } | null;
  perLesson: number | null;
}

/** «Оплата: <клиент>». Replaces the old «Получено» sheet on the Today card and the lesson panel. */
export function LessonPaySheet({ bookingId, name, onClose, onDone }: {
  bookingId: string; name: string; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [info, setInfo] = useState<Info | null>(null);
  const [useBalance, setUseBalance] = useState(true);
  const [withNext, setWithNext] = useState(false);
  const [value, setValue] = useState<string | null>(null);
  const [method, setMethod] = useMethod();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    api<Info>(`/api/coach/bookings/${bookingId}/settle-info`).then(setInfo).catch((e) => setError(receiptErrorText(t, e.message)));
  }, [bookingId]);

  const hasBalance = !!info && info.balance.credits > 0 && info.owed > 0;
  const fromBalance = hasBalance && useBalance ? info!.balance.value : 0;
  const rest = info ? r2(Math.max(0, info.owed - fromBalance)) : 0;
  // «+ Оплатить следующее занятие», or with nothing booked, one lesson on the balance.
  const extra = !info ? 0 : info.next ? info.next.price : info.perLesson ?? 0;
  const suggested = r2(rest + (withNext ? extra : 0));
  const amount = value === null ? suggested : Number(value.replace(',', '.'));
  const valid = Number.isFinite(amount) && amount >= 0;

  // Where every euro goes, as the server will put it (receipts.ts, settleLesson).
  const plan = useMemo(() => {
    if (!info || !valid) return null;
    let left = r2(amount);
    const today = r2(Math.min(left, rest)); left = r2(left - today);
    let next = 0;
    if (withNext && info.next && left > 0) { next = r2(Math.min(left, info.next.price)); left = r2(left - next); }
    const per = info.next && withNext && info.next.mode === 'credits' ? info.next.price / Math.max(info.next.credits, 0.0001) : (info.perLesson ?? 0);
    return { today, next, toBalance: left, short: r2(rest - today), perCredit: per };
  }, [info, amount, valid, rest, withNext]);

  const nothing = !plan || (amount === 0 && fromBalance === 0);
  const creditsLeft = info ? Math.max(0, info.balance.credits - (useBalance && hasBalance ? info.balance.covers : 0)) : 0;
  const nextDay = info?.next ? dayShort(info.next.startsAt) : '';

  async function save() {
    if (!info || !valid || nothing) return;
    setBusy(true); setError(null);
    try {
      await api(`/api/coach/bookings/${bookingId}/settle`, {
        method: 'POST',
        body: JSON.stringify({
          amount, method: amount > 0 ? method : undefined, useBalance: hasBalance && useBalance,
          prepay: withNext && info.next ? { bookingId: info.next.bookingId } : null,
        }),
      });
      if (amount > 0) rememberMethod(method);
      haptic.success();
      onDone();
      onClose();
    } catch (e: any) {
      haptic.error();
      setError(receiptErrorText(t, e.message));
    } finally { setBusy(false); }
  }

  const parts: string[] = [];
  if (plan) {
    if (plan.today > 0) parts.push(t('lp.partToday', { amount: fmtMoney(plan.today) }));
    if (plan.next > 0 && info?.next) {
      parts.push(info.next.mode === 'cash' && plan.next < info.next.price
        ? t('lp.partNextShort', { amount: fmtMoney(plan.next), day: nextDay, left: fmtMoney(r2(info.next.price - plan.next)) })
        : t('lp.partNext', { amount: fmtMoney(plan.next), day: nextDay }));
    }
    if (plan.toBalance > 0) parts.push(t('lp.partBalance', { amount: fmtMoney(plan.toBalance), n: cr(plan.perCredit > 0 ? plan.toBalance / plan.perCredit : 0) }));
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel lpay" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('co.paid.title', { name })}</div>
        {info && <div className="card__when">{fmtLongDay(new Date(info.startsAt))}, {fmtTime(info.startsAt)} · {t('bookings.private', { min: info.minutes })}</div>}
        {!info && !error && <div className="card__when">…</div>}
        {info && (<>
          <div className="lpay__due"><span>{t('co.paid.due')}</span><b>{fmtMoney(info.owed)}</b></div>

          {hasBalance && (<>
            <label className="lpay__tick">
              <input type="checkbox" checked={useBalance} onChange={(e) => { haptic.tap(); setUseBalance(e.target.checked); setValue(null); }} />
              <span className="lpay__tickMain">{t('lp.useBalance')}
                <small>{info.balance.payerName
                  ? t('lp.balanceSubPayer', { payer: info.balance.payerName, n: cr(info.balance.credits) })
                  : t('lp.balanceSub', { n: cr(info.balance.credits), rate: fmtMoney(info.balance.perCredit ?? 0) })}</small>
              </span>
              <span className="lpay__amt">−{fmtMoney(info.balance.value)}</span>
            </label>
            {useBalance && <div className="lpay__rest"><span>{t('lp.rest')}</span><b>{fmtMoney(rest)}</b></div>}
          </>)}

          <div className="lpay__got">
            <span>{t('co.paid.received')}</span>
            <label><input type="text" inputMode="decimal" value={value ?? String(suggested)}
              onChange={(e) => setValue(e.target.value)} /><span>€</span></label>
          </div>
          <div className="paid__quick">
            {[...new Set([rest, r2(rest + extra)])].filter((q) => q > 0).map((q) => (
              <button key={q} className={`chip${amount === q ? ' chip--on' : ''}`} aria-pressed={amount === q}
                onClick={() => { haptic.tap(); setValue(String(q)); setWithNext(q > rest); }}>{fmtMoney(q)}</button>
            ))}
            <button className="chip" disabled={busy} onClick={() => { haptic.tap(); setSwitching(true); }}>{t('co.sw.useCredit')}</button>
          </div>

          {extra > 0 && (
            <label className="lpay__tick">
              <input type="checkbox" checked={withNext} onChange={(e) => { haptic.tap(); setWithNext(e.target.checked); setValue(null); }} />
              {info.next ? (
                <span className="lpay__tickMain">{t('lp.next')}
                  <small>{fmtShortDay(new Date(info.next.startsAt))}, {fmtTime(info.next.startsAt)} · {info.next.title}
                    {info.next.mode === 'credits' ? ` · ${t('lp.nextCreditsSub', { n: cr(info.next.credits) })}` : ''}</small>
                </span>
              ) : (
                <span className="lpay__tickMain">{t('lp.toBalance', { amount: fmtMoney(extra), n: cr(1) })}
                  <small>{t('lp.toBalanceSub')}</small>
                </span>
              )}
              <span className="lpay__amt">{fmtMoney(extra)}</span>
            </label>
          )}

          {amount > 0 && <div style={{ marginTop: 12 }}><MethodPicker value={method} onChange={setMethod} /></div>}

          {plan && !nothing && (
            <div className="lpay__total" role="status">
              {amount > 0
                ? <><b>{t('lp.total', { amount: fmtMoney(amount), method: methodLabel(t, method) })}</b>{parts.length > 0 && <> = {parts.join(' + ')}</>}</>
                : <b>{t('lp.saveBalance')}</b>}
              {fromBalance > 0 && <small>{t('lp.fromBalance', { n: cr(info.balance.covers), left: cr(creditsLeft) })}</small>}
            </div>
          )}
          {plan && plan.short >= 1 && <div className="warnline">{t('lp.short', { left: fmtMoney(plan.short) })}</div>}
        </>)}
        {error && <div className="warnline">{error}</div>}
        <div className="sheet__actions">
          <button className="paybar__opt" onClick={onClose}>{t('co.offer.cancel')}</button>
          <button className="savebtn" disabled={busy || !info || !valid || nothing} onClick={save}>
            {amount > 0 ? t('lp.save', { amount: fmtMoney(amount) }) : t('lp.saveBalance')}
          </button>
        </div>
        {switching && (
          <PaySwitchSheet bookingId={bookingId}
            onClose={() => setSwitching(false)}
            onDone={() => { onDone(); onClose(); }} />
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * «К оплате 80 € · Получено…» at the top of a lesson that is still owed,
 * wherever the lesson is opened (the past lesson page). The button opens
 * the same «Оплата» sheet as the lesson panel: balance first, method,
 * «+ Оплатить следующее занятие».
 */
export function LessonPayBlock({ bookingId, name, owed, onDone }: {
  bookingId: string; name: string; owed: number; onDone: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (!(owed > 0)) return null;
  return (
    <div className="card lpayblock">
      <span className="lpayblock__due">{t('lp.due', { amount: fmtMoney(owed) })}</span>
      <button className="act act--cash" onClick={() => { haptic.tap(); setOpen(true); }}>{t('co.today.gotDots')}</button>
      {open && <LessonPaySheet bookingId={bookingId} name={name} onClose={() => setOpen(false)} onDone={onDone} />}
    </div>
  );
}

interface PaymentView {
  rows: { id: string; amount: number; method: PayMethod | null; receivedAt: string; receiptId: string | null }[];
  credits: number;
  receipt: { id: string; amount: number; method: PayMethod | null; creditsUsed: number; at: string; who: string | null; undone: boolean;
    parts: { bookingId: string; amount: number; startsAt: string }[]; toBalance: number } | null;
  editable: boolean; who: string | null;
  changes: { action: 'method' | 'amount' | 'undo'; old: any; new: any; at: string; who: string | null }[];
}

const changeText = (t: T, c: { action: string; old: any; new: any }) =>
  c.action === 'method' ? t('lp.ch.method', { old: methodLabel(t, c.old?.method ?? 'none'), new: methodLabel(t, c.new?.method ?? 'none') })
    : c.action === 'amount' ? t('lp.ch.amount', { old: fmtMoney(c.old?.amount ?? 0), new: fmtMoney(c.new?.amount ?? 0) })
    : t('lp.ch.undo');
const when = (iso: string) => `${dayShort(iso)}, ${fmtTime(iso)}`;

/** «Изменить оплату»: a paid lesson's amount and method, or undo the whole payment. */
export function EditPaymentSheet({ bookingId, name, onClose, onDone }: {
  bookingId: string; name: string; onClose: () => void; onDone: () => void;
}) {
  const t = useT();
  const [v, setV] = useState<PaymentView | null>(null);
  const [value, setValue] = useState('');
  const [method, setMethod] = useState<PayMethod>('cash');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<PaymentView>(`/api/coach/bookings/${bookingId}/payment`).then((r) => {
      setV(r);
      const row = r.rows[r.rows.length - 1];
      if (row) { setValue(String(row.amount)); setMethod(row.method ?? 'cash'); }
    }).catch((e) => setError(receiptErrorText(t, e.message)));
  }, [bookingId]);
  const row = v?.rows[v.rows.length - 1] ?? null;
  const amount = Number(value.replace(',', '.'));
  const dirty = !!row && ((Number.isFinite(amount) && amount !== row.amount) || method !== (row.method ?? 'cash'));
  const total = v?.receipt ? v.receipt.amount : row?.amount ?? 0;
  const others = (v?.receipt?.parts ?? []).filter((p) => p.bookingId !== bookingId);

  async function save() {
    if (!row || !dirty) return;
    setBusy(true); setError(null);
    try {
      await api(`/api/coach/cash-payments/${row.id}/edit`, {
        method: 'POST',
        body: JSON.stringify({ ...(amount !== row.amount ? { amount } : {}), ...(method !== row.method ? { method } : {}) }),
      });
      haptic.success(); onDone(); onClose();
    } catch (e: any) { haptic.error(); setError(receiptErrorText(t, e.message)); } finally { setBusy(false); }
  }
  async function undo() {
    if (!v) return;
    if (!(await confirm(t('lp.undoConfirm', { amount: fmtMoney(total) })))) return;
    setBusy(true); setError(null);
    try {
      await api(v.receipt ? `/api/coach/receipts/${v.receipt.id}/undo` : `/api/coach/cash-payments/${row!.id}/undo`, { method: 'POST', body: '{}' });
      haptic.success(); onDone(); onClose();
    } catch (e: any) {
      haptic.error();
      const text = receiptErrorText(t, e.message);
      setError(text);
      await alert(text);
    } finally { setBusy(false); }
  }

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel lpay" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">{t('lp.editTitle', { name })}</div>
        {!v && !error && <div className="card__when">…</div>}
        {v && (<>
          <div className="lpay__was">
            <b>{t('lp.paidBtn')}</b>
            {' · '}{[...v.rows.map((r) => `${fmtMoney(r.amount)} ${r.method ? methodLabel(t, r.method) : ''}`.trim()),
              ...(v.credits > 0 ? [t('lp.byCredits', { n: cr(v.credits) })] : [])].join(' + ')}
            {(v.receipt || row) && (
              <small>{t('lp.receivedBy', { at: when(v.receipt?.at ?? row!.receivedAt), who: v.receipt?.who ?? v.who ?? '—' })}
                {others.length > 0 && ` · ${t('lp.sameReceipt', { parts: others.map((p) => `${fmtMoney(p.amount)} — ${dayShort(p.startsAt)}`).join(', ') })}`}
              </small>
            )}
          </div>
          {!v.editable && <div className="warnline">{t('lp.err.read_only')}</div>}
          {v.editable && row && (<>
            <div className="lpay__got">
              <span>{t('lp.amountThis')}</span>
              <label><input type="text" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} /><span>€</span></label>
            </div>
            <div className="card__when" style={{ marginTop: 4 }}>{t('lp.amountHint')}</div>
            <div style={{ marginTop: 12 }}><MethodPicker value={method} onChange={setMethod} /></div>
            {v.receipt && v.receipt.amount !== row.amount && <div className="card__when" style={{ marginTop: 4 }}>{t('lp.methodAll', { amount: fmtMoney(v.receipt.amount) })}</div>}
          </>)}
          {v.editable && (row || v.receipt) && (
            <button className="lpay__undo" disabled={busy} onClick={undo}>{t('lp.undo', { amount: fmtMoney(total) })}</button>
          )}
        </>)}
        {error && <div className="warnline">{error}</div>}
        <div className="sheet__actions">
          <button className="paybar__opt" onClick={onClose}>{t('lp.back')}</button>
          {v?.editable && row && <button className="savebtn" disabled={busy || !dirty} onClick={save}>{t('lp.saveChange')}</button>}
        </div>
        {v && v.changes.length > 0 && (
          <div className="lpay__audit">
            <div className="lpay__auditTitle">{t('lp.changes')}</div>
            {v.changes.map((c, i) => <div key={i}>{when(c.at)} · {c.who ?? '—'} · {changeText(t, c)}</div>)}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

type Item = any;
type Filter = 'all' | 'money' | 'credits' | 'changes';
const MONEY = new Set(['receipt', 'cash', 'topup', 'package', 'overpay']);
const CREDITS = new Set(['use', 'refund', 'expiry', 'adjust']);

/** «История оплат»: one dated list for a client, newest first (server: paymentHistory). */
export function PaymentHistory({ clientId, name, onBack }: { clientId: string; name: string; onBack: () => void }) {
  const t = useT();
  const [data, setData] = useState<{ items: Item[]; balance: number } | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [error, setError] = useState<string | null>(null);
  // A payment's row opens «Изменить оплату» for its (first) lesson.
  const [editFor, setEditFor] = useState<string | null>(null);
  const load = () => {
    api<{ items: Item[]; balance: number }>(`/api/coach/clients/${clientId}/payment-history`).then(setData).catch((e) => setError(e.message));
  };
  useEffect(load, [clientId]);
  // A screen of its own: from the top, not where the card was scrolled to.
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const shown = (data?.items ?? []).filter((x) => filter === 'all' || (filter === 'money' ? MONEY.has(x.kind)
    : filter === 'credits' ? CREDITS.has(x.kind) || (x.kind === 'receipt' && x.creditsUsed > 0) : x.kind === 'change'));
  const days: [string, Item[]][] = [];
  for (const x of shown) {
    const k = fmtLongDay(new Date(x.at));
    const last = days[days.length - 1];
    if (last && last[0] === k) last[1].push(x); else days.push([k, [x]]);
  }

  return (
    <div className="lphist" data-layer>
      <button className="lphist__back" data-back onClick={onBack}>‹ {name}</button>
      <div className="lphist__head"><h1>{t('lp.history')}</h1>{data && <span>{t('lp.onBalance', { n: cr(data.balance) })}</span>}</div>
      <div className="lphist__filter">
        {(['all', 'money', 'credits', 'changes'] as Filter[]).map((f) => (
          <button key={f} className="chip" aria-pressed={filter === f} onClick={() => { haptic.tap(); setFilter(f); }}>{t(`lp.f.${f}` as 'lp.f.all')}</button>
        ))}
      </div>
      {error && <div className="warnline" style={{ margin: 16 }}>{error}</div>}
      {data && shown.length === 0 && <div className="empty" style={{ padding: 28 }}>{t('lp.h.empty')}</div>}
      {days.map(([d, xs]) => (
        <div key={d}>
          <div className="lphist__day">{d}</div>
          {xs.map((x) => {
            const lesson = x.kind === 'receipt' && !x.undone ? x.parts?.[0]?.bookingId : x.kind === 'cash' ? x.bookingId : null;
            return <HistoryRow key={`${x.kind}-${x.id}`} x={x} t={t} onTap={lesson ? () => { haptic.tap(); setEditFor(lesson); } : undefined} />;
          })}
        </div>
      ))}
      {editFor && <EditPaymentSheet bookingId={editFor} name={name} onClose={() => setEditFor(null)} onDone={load} />}
    </div>
  );
}

function HistoryRow({ x, t, onTap }: { x: Item; t: T; onTap?: () => void }) {
  const m = (method: PayMethod | null) => (method ? methodLabel(t, method) : methodLabel(t, 'none'));
  let icon = '€', tone = 'plus', main = '', sub = '', amt = '', amtSub = '';
  switch (x.kind) {
    case 'receipt': {
      main = x.amount > 0 ? t('lp.h.received', { amount: fmtMoney(x.amount) }) : t('lp.saveBalance');
      const parts = (x.parts as any[]).map((p) => {
        const bits: string[] = [];
        if (p.cash > 0) bits.push(p.prepaid ? t('lp.h.partAhead', { amount: fmtMoney(p.cash), day: dayShort(p.startsAt) })
          : t('lp.h.partCash', { amount: fmtMoney(p.cash), day: dayShort(p.startsAt) }));
        if (p.credits > 0) bits.push(t('lp.h.partCredits', { n: cr(p.credits) }) + (p.cash > 0 ? '' : ` · ${t('lp.h.lesson', { day: dayShort(p.startsAt) })}`));
        return bits.join(' + ');
      });
      for (const b of x.toBalance as any[]) parts.push(t('lp.h.partToBalance', { amount: fmtMoney(b.amount), n: cr(b.credits) }) + (b.forAt ? t('lp.h.forLesson', { day: dayShort(b.forAt) }) : ''));
      sub = parts.join(' · ') + (x.who ? ` · ${x.who}` : '');
      if (x.undone) { tone = 'off'; sub += ` · ${t('lp.h.undone')}`; }
      amt = x.amount > 0 ? `+${fmtMoney(x.amount)}` : `−${cr(x.creditsUsed)}`; amtSub = x.amount > 0 ? m(x.method) : '';
      break;
    }
    case 'cash':
      main = t('lp.h.received', { amount: fmtMoney(x.amount) }); sub = t('lp.h.lesson', { day: dayShort(x.startsAt) });
      amt = `+${fmtMoney(x.amount)}`; amtSub = m(x.method); break;
    case 'topup': case 'package': case 'overpay':
      icon = '+';
      main = x.kind === 'package' ? t('lp.h.package', { name: x.name, amount: fmtMoney(x.amount ?? 0) })
        : t(x.kind === 'topup' ? 'lp.h.topup' : 'lp.h.overpay', { amount: fmtMoney(x.amount ?? 0), n: cr(x.credits) });
      if (x.refunded) { tone = 'off'; sub = t('lp.h.refunded'); }
      amt = (x.amount ?? 0) > 0 ? `+${fmtMoney(x.amount)}` : `+${cr(x.credits)}`; amtSub = (x.amount ?? 0) > 0 ? m(x.method) : '';
      break;
    case 'use': case 'expiry': case 'adjust': case 'refund':
      icon = x.kind === 'refund' ? '↺' : '−'; tone = x.delta > 0 ? 'plus' : 'minus';
      main = t(`lp.h.${x.kind}` as 'lp.h.use', { n: cr(x.credits) });
      sub = x.startsAt ? t('lp.h.lesson', { day: `${dayShort(x.startsAt)}, ${fmtTime(x.startsAt)}` }) : (x.note ?? '');
      amt = `${x.delta > 0 ? '+' : '−'}${cr(x.credits)}`; break;
    case 'change':
      icon = '✎'; tone = 'chg';
      main = t('lp.h.change', { what: changeText(t, x) });
      sub = [x.who, fmtTime(x.at), x.startsAt ? t('lp.h.lesson', { day: dayShort(x.startsAt) }) : ''].filter(Boolean).join(' · ');
      break;
  }
  const body = (<>
    <span className="lphist__ic">{icon}</span>
    <span className="lphist__main">{main}{sub && <small>{sub}</small>}</span>
    {amt && <span className="lphist__amt">{amt}{amtSub && <small>{amtSub}</small>}</span>}
  </>);
  return onTap
    ? <button className={`lphist__row lphist__row--${tone} lphist__row--tap`} onClick={onTap} aria-label={`${main} · ${t('lp.edit')}`}>{body}</button>
    : <div className={`lphist__row lphist__row--${tone}`}>{body}</div>;
}
